import { z } from 'zod';

import i18n from '@/lib/i18n';
import { createPostalCodeRefinement, mainstreamCountrySchema } from '@/services/api/mainstream';

const identityAddressOfResidencySchema = z.object({
  addressLines: z.array(z.string().min(1)).min(1).max(2),
  country: mainstreamCountrySchema(),
  locality: z.string().min(1),
  postalCode: z.string(),
});

export type IdentityAddressOfResidency = z.infer<typeof identityAddressOfResidencySchema>;

export const TAX_RESIDENCY_SOURCE = {
  KYC: 'kyc',
  ONBOARDING: 'onboarding',
} as const;

const identityTaxResidencySchema = z.object({
  affirmedAt: z.iso.datetime().optional(),
  residences: z
    .array(
      z.object({
        country: mainstreamCountrySchema(),
        taxId: z.string().min(1).optional(),
      }),
    )
    .min(1),
  source: z.enum(TAX_RESIDENCY_SOURCE).optional(),
});

const { refinement: postalCodeRefinement, params: postalCodeRefinementParams } = createPostalCodeRefinement<{
  addressOfResidency?: Pick<IdentityAddressOfResidency, 'country' | 'postalCode'>;
}>({
  getParts: (data) => ({ country: data?.addressOfResidency?.country, postCode: data?.addressOfResidency?.postalCode }),
  message: i18n.t('requiredField.error'),
  path: ['addressOfResidency', 'postalCode'],
});

export const identitySchema = z
  .object({
    addressOfResidency: identityAddressOfResidencySchema,
    birthInfo: z.object({
      country: mainstreamCountrySchema(),
      date: z.iso.date(),
      locality: z.string().min(1),
    }),
    citizenship: mainstreamCountrySchema(),
    citizenship2: mainstreamCountrySchema().optional(),
    firstName: z.string().min(1),
    lastName: z.string().min(1),
    phoneNumber: z.object({
      areaCode: z.string().min(1),
      mobilePhone: z.string().min(1),
    }),
    /** @deprecated Superseded by taxResidency; sent back unchanged so the stored value is kept until it is retired */
    taxId: z.string().optional(),
    taxResidency: identityTaxResidencySchema,
  })
  .refine(postalCodeRefinement, postalCodeRefinementParams);

export type Identity = z.infer<typeof identitySchema>;

export const identityWithFallbacksSchema = z.object({
  addressOfResidency: z
    .object({
      addressLines: z.array(z.string().min(1)).min(1).max(2).catch([]),
      country: mainstreamCountrySchema().catch(''),
      locality: z.string().min(1).catch(''),
      postalCode: z.string().min(1).catch(''),
    })
    .catch({
      addressLines: [],
      country: '',
      locality: '',
      postalCode: '',
    }),
  birthInfo: z
    .object({
      country: mainstreamCountrySchema().catch(''),
      date: z.iso.date().catch(''),
      locality: z.string().min(1).catch(''),
    })
    .catch({
      country: '',
      date: '',
      locality: '',
    }),
  citizenship: mainstreamCountrySchema().catch(''),
  citizenship2: mainstreamCountrySchema().optional().catch(undefined),
  firstName: z.string().min(1).catch(''),
  lastName: z.string().min(1).catch(''),
  phoneNumber: z
    .object({
      areaCode: z.string().min(1).catch(''),
      mobilePhone: z.string().min(1).catch(''),
    })
    .catch({
      areaCode: '',
      mobilePhone: '',
    }),
  taxId: z.string().optional().catch(undefined),
  taxResidency: identityTaxResidencySchema.optional().catch(undefined),
});

export type IdentityWithFallbacks = z.infer<typeof identityWithFallbacksSchema>;
